<template>
  <intersect :threshold="[0, 0.5]" @enter.once="enter" ref="imageEl">
    <div class="o-0" :class="classes">
      <div
        class="dn-l absolute z-2 top-0 cover bg-center right-0 w2 h2 ma2"
        :class="`bg-zoom-icon--${this.themeSite}`"
      ></div>

      <img
        :src="placeholderSrc"
        :data-src="src"
        :alt="alt"
        :data-zoom-src="zoomSrc"
        ref="zoom"
        :class="imgClasses"
        class="v-mid pen mw-none w-100 lazyload"
      />

      <button
        class="dn dib-l absolute absolute--fill w-100 h-100 z-2 light bg-transparent bn outline-0"
        :class="`custom-cursor--${this.themeSite}`"
        type="button"
      ></button>
    </div>
  </intersect>
</template>

<script>
import s from "../store"
import anime from "animejs"
import mediumZoom from "medium-zoom"
import Intersect from "vue-intersect"

export default {
  name: "ImageZoom",

  components: { Intersect },

  data() {
    return {
      sharedState: s.state,
      zoomInstance: null,
      themeBackgroundColor: null,
      themeColor: null
    }
  },

  props: {
    placeholderSrc: String,
    src: String,
    zoomSrc: String,
    alt: String,
    imgClasses: {
      type: String,
      default: ""
    },
    imgIndex: {
      type: String
    },
    classes: {
      type: String,
      default: "relative"
    },
    themeSite: {
      type: String,
      default: "portal"
    }
  },

  created() {
    if (s.debug) {
      console.log("init Vue component ImageZoom")
    }
  },

  mounted() {
    this.setColors(this.themeSite)

    this.zoomInstance = mediumZoom(this.$refs.zoom, {
      margin: 24,
      background: this.themeBackgroundColor
    })

    this.generateCloseCursor(this.themeColor)
  },

  methods: {
    enter: function() {
      const _self = this
      const i = _self.imgIndex
      anime({
        targets: this.$el,
        opacity: [0, 1],
        translateY: [64, 0],
        duration: 750,
        delay: function(el, i, l) {
          return 200 + i * 50
        },
        easing: "easeOutCubic"
      })
    },
    hexToRGB: function(h) {
      let r = 0,
        g = 0,
        b = 0
      // 3 digits
      if (h.length == 4) {
        r = "0x" + h[1] + h[1]
        g = "0x" + h[2] + h[2]
        b = "0x" + h[3] + h[3]
        // 6 digits
      } else if (h.length == 7) {
        r = "0x" + h[1] + h[2]
        g = "0x" + h[3] + h[4]
        b = "0x" + h[5] + h[6]
      }
      return "" + +r + "," + +g + "," + +b + ""
    },

    setColors: function(site) {
      const setColor = s.static.theme[site].colors.color2
      const setBgColor = s.static.theme[site].colors.color1
      this.themeBackgroundColor = `rgba(${this.hexToRGB(setBgColor)}, 0.8)`
      this.themeColor = setColor
    },

    onClick: function(event) {
      if (!this.zoomInstance) {
        return
      }
      this.zoomInstance.open()
    },

    generateCloseCursor: function(cursorBgColor = "#E1A724") {
      // Check if cursor style already present in DOM
      const cursorStyleId = "medium-zoom-cursor"
      if (document.getElementById(cursorStyleId)) {
        return
      }
      // Generate close cursor inline svg
      const cursor = `<svg height="80" viewBox="0 0 80 80" width="80" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <g fill="none" fill-rule="evenodd">
              <circle fill="${cursorBgColor}" cx="40" cy="40" r="40"/>
              <path d="m0 0h80.186916v80.186916h-80.186916z"/>
              <g fill="#fff">
                  <g transform="matrix(.70710678 .70710678 -.70710678 .70710678 40 21.20101)">
                      <path d="m0 12.444444h28v3.111111h-28z"/>
                      <path d="m0 12.444444h28v3.111111h-28z" transform="matrix(0 1 -1 0 28 0)"/>
                  </g>
              </g>
          </g>
      </svg>`
      // Inject CSS into DOM
      const style = document.createElement("style")
      style.id = cursorStyleId
      style.innerHTML = `.medium-zoom--opened .medium-zoom-overlay {
        cursor: url("data:image/svg+xml;base64,${window.btoa(
          cursor
        )}"), pointer;
      }`
      document.body.appendChild(style)
    }
  }
}
</script>

<style>
.medium-zoom-overlay {
  z-index: 9998;
}

.medium-zoom-image--opened {
  z-index: 9999;
}

.custom-cursor--fizeaustraat {
  cursor: url("../../assets/cursor-fizeaustraat.cur"),
    url("../../assets/cursor-fizeaustraat.png"), auto;
  cursor: -webkit-image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
  cursor: image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
}

.custom-cursor--frederiksplein {
  cursor: url("../../assets/cursor-frederiksplein.cur"),
    url("../../assets/cursor-frederiksplein.png"), auto;
  cursor: -webkit-image-set(
      url("../../assets/cursor-frederiksplein.png") 1x,
      url("../../assets/cursor-frederiksplein-2x.png") 2x
    ),
    auto;
  cursor: image-set(
      url("../../assets/cursor-frederiksplein.png") 1x,
      url("../../assets/cursor-frederiksplein-2x.png") 2x
    ),
    auto;
}

.custom-cursor--nesplein {
  cursor: url("../../assets/cursor-nesplein.cur"),
    url("../../assets/cursor-nesplein.png"), auto;
  cursor: -webkit-image-set(
      url("../../assets/cursor-nesplein.png") 1x,
      url("../../assets/cursor-nesplein-2x.png") 2x
    ),
    auto;
  cursor: image-set(
      url("../../assets/cursor-nesplein.png") 1x,
      url("../../assets/cursor-nesplein-2x.png") 2x
    ),
    auto;
}

.custom-cursor--portal {
  cursor: url("../../assets/cursor-portal.cur"),
    url("../../assets/cursor-portal.png"), auto;
  cursor: -webkit-image-set(
      url("../../assets/cursor-portal.png") 1x,
      url("../../assets/cursor-portal-2x.png") 2x
    ),
    auto;
  cursor: image-set(
      url("../../assets/cursor-portal.png") 1x,
      url("../../assets/cursor-portal-2x.png") 2x
    ),
    auto;
}

.bg-zoom-icon--fizeaustraat {
  background-image: -webkit-image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
  background-image: image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
}

.bg-zoom-icon--frederiksplein {
  background-image: -webkit-image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
  background-image: image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
}

.bg-zoom-icon--nesplein {
  background-image: -webkit-image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
  background-image: image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
}

.bg-zoom-icon--portal {
  background-image: -webkit-image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
  background-image: image-set(
      url("../../assets/cursor-fizeaustraat.png") 1x,
      url("../../assets/cursor-fizeaustraat-2x.png") 2x
    ),
    auto;
}
</style>
